import React, { useState } from 'react';
import { Box, TextField, Button, Popover, Typography, IconButton } from '@mui/material';
import { CalendarMonth as CalendarIcon, Clear as ClearIcon } from '@mui/icons-material';

interface DateRangePickerProps {
    fromDate?: string;
    toDate?: string;
    onDateChange: (from: string, to: string) => void;
    onClear: () => void;
    variant?: 'button' | 'inline';
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    fromDate,
    toDate,
    onDateChange,
    onClear,
    variant = 'button'
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [tempFromDate, setTempFromDate] = useState<Date | null>(
        fromDate ? new Date(fromDate) : null
    );
    const [tempToDate, setTempToDate] = useState<Date | null>(
        toDate ? new Date(toDate) : null
    );
    const [inlineFrom, setInlineFrom] = useState(fromDate ?? '');
    const [inlineTo, setInlineTo] = useState(toDate ?? '');

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        if (tempFromDate && tempToDate) {
            const fromStr = tempFromDate.toISOString().split('T')[0];
            const toStr = tempToDate.toISOString().split('T')[0];
            onDateChange(fromStr, toStr);
        }
        handleClose();
    };

    const handleClear = () => {
        setTempFromDate(null);
        setTempToDate(null);
        onClear();
        setInlineFrom('');
        setInlineTo('');
        handleClose();
    };

    const handleInlineChange = (field: 'from' | 'to') => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const nextFrom = field === 'from' ? value : inlineFrom;
        const nextTo = field === 'to' ? value : inlineTo;

        setInlineFrom(nextFrom);
        setInlineTo(nextTo);

        if (nextFrom && nextTo) {
            onDateChange(nextFrom, nextTo);
        } else if (!nextFrom || !nextTo) {
            onClear();
        }
    };

    const handleInlineClear = () => {
        setInlineFrom('');
        setInlineTo('');
        onClear();
    };

    React.useEffect(() => {
        setInlineFrom(fromDate ?? '');
    }, [fromDate]);

    React.useEffect(() => {
        setInlineTo(toDate ?? '');
    }, [toDate]);

    const getDisplayText = () => {
        if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
                return `${from.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })} - ${to.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`;
            }
        }
        return 'Select Date Range';
    };

    if (variant === 'inline') {
        return (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField
                    label="From"
                    type="date"
                    value={inlineFrom}
                    onChange={handleInlineChange('from')}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <TextField
                    label="To"
                    type="date"
                    value={inlineTo}
                    onChange={handleInlineChange('to')}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleInlineClear}
                    disabled={!inlineFrom && !inlineTo}
                    startIcon={<ClearIcon fontSize="small" />}
                >
                    Clear
                </Button>
            </Box>
        );
    }

    const open = Boolean(anchorEl);

    return (
        <Box>
            <Button
                variant="outlined"
                fullWidth
                startIcon={<CalendarIcon />}
                onClick={handleClick}
                sx={(theme) => ({
                    height: 56,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    color: theme.palette.text.primary,
                    borderRadius: 1,
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                    }
                })}
            >
                {getDisplayText()}
            </Button>
            {(fromDate || toDate) && (
                <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={(theme) => ({ ml: 1, color: theme.palette.text.secondary })}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            )}
            
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, minWidth: 280 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Select Date Range
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                        <TextField
                            label="From Date"
                            type="date"
                            value={tempFromDate ? tempFromDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempFromDate(e.target.value ? new Date(e.target.value) : null)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="To Date"
                            type="date"
                            value={tempToDate ? tempToDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempToDate(e.target.value ? new Date(e.target.value) : null)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            fullWidth
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={handleClose} size="small">
                            Cancel
                        </Button>
                        <Button onClick={handleClear} size="small" color="secondary">
                            Clear
                        </Button>
                        <Button 
                            onClick={handleApply} 
                            variant="contained" 
                            size="small"
                            disabled={!tempFromDate || !tempToDate}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default DateRangePicker;
